import styled from "@emotion/styled";
import React from "react";

import { getModuleThemeColors } from "@brexhq/next/src/components/pageBuilder/ModuleLayout";
import { Link } from "@brexhq/next/src/components/shared/Link";
import {
  BodyS,
  SubtitleL,
} from "@brexhq/shared/components/Typography/Components";
import { Text } from "@brexhq/shared/components/Typography/Text";
import { ModuleThemeType } from "@brexhq/shared/types/ModuleTheme";
import { ComparisonTableColumnHeader } from "@brexhq/shared/types/modules/BlockRowsModule/BlockRowItems/ComparisonTable";

import { CellContent } from "./ComparisonTableCell";
import { Row } from "./ComparisonTableRow";

interface Props {
  headerColumnTitle?: string;
  columnHeaders: ComparisonTableColumnHeader[];
  theme: ModuleThemeType;
  withBorder?: boolean;
}

const DesktopHeaderRow = styled(Row)(({ theme }) => ({
  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

export const MobileHeaderRow = styled(Row)(({ theme }) => ({
  display: "none",
  [theme.breakpoints.SML]: {
    display: "table-row",
  },
}));

const HeaderCell = styled.th<{
  isMainHeaderCell?: boolean;
  headerColWidth?: string;
}>(({ isMainHeaderCell, headerColWidth, theme }) => ({
  padding: 0,
  margin: 0,
  ["h3, h4"]: {
    whiteSpace: "nowrap",
  },
  height: "88px",
  width: isMainHeaderCell ? headerColWidth : undefined,
  [theme.breakpoints.SML]: {
    height: "auto",
  },
}));

export const CTALink = styled(Link)({
  margin: "8px auto 0",
  padding: "8px 16px",
  height: "40px",
  // can't use theme breakpoints here because of the "modifier" prop conflict
  ["@media (max-width: 1439px)"]: {
    minWidth: "unset",
    height: "36px",
  },
  ["@media (max-width: 1023px)"]: {
    padding: "8px",
    height: "34px",
  },
  ["@media (max-width: 766px)"]: {
    height: "32px",
  },
});

export const ComparisonTableHeaderRow = ({
  headerColumnTitle,
  columnHeaders,
  theme,
  withBorder = true,
}: Props) => {
  const {
    standard: { textPrimary, textSecondary, background, borders },
  } = getModuleThemeColors(theme);

  return (
    <>
      <DesktopHeaderRow backgroundColor={background}>
        <HeaderCell
          isMainHeaderCell
          // If there is only one data column, fallback to standard table layout so the header col and data col take up equal space
          headerColWidth={columnHeaders.length > 1 ? "25%" : undefined}
        >
          <CellContent
            isMainHeaderCell
            borders={withBorder ? borders : undefined}
          >
            <SubtitleL as="h3" color={textPrimary}>
              {headerColumnTitle}
            </SubtitleL>
          </CellContent>
        </HeaderCell>
        {columnHeaders.map(
          ({ columnHeaderTitle, columnHeaderDescription, columnHeaderCTA }) => (
            <HeaderCell key={columnHeaderTitle}>
              <CellContent borders={withBorder ? borders : undefined}>
                <Text
                  as="h4"
                  color={textPrimary}
                  components={{
                    LRG: "Subtitle1", //SubtitleL
                    MED: "Subtitle2", //SubtitleS
                    SML: "Subtitle2",
                  }}
                >
                  {columnHeaderTitle}
                </Text>
                <BodyS color={textSecondary}>{columnHeaderDescription}</BodyS>
                {columnHeaderCTA?.text && (
                  <CTALink
                    modifier="primary"
                    to={
                      columnHeaderCTA.link?.to ||
                      columnHeaderCTA.link?.toExternal ||
                      columnHeaderCTA.link?.hash ||
                      "#"
                    }
                    noFollow={columnHeaderCTA.link?.noFollow}
                    label={columnHeaderCTA.text}
                    component={`card-${headerColumnTitle}-${columnHeaderTitle}-bottom-cta`}
                  />
                )}
              </CellContent>
            </HeaderCell>
          ),
        )}
      </DesktopHeaderRow>
      <MobileHeaderRow backgroundColor={background}>
        {columnHeaders.map(({ columnHeaderTitle, columnHeaderCTA }) => (
          <HeaderCell key={columnHeaderTitle}>
            <CellContent borders={borders}>
              <BodyS as="h4" color={textPrimary}>
                {columnHeaderTitle}
              </BodyS>
              {columnHeaderCTA?.text && (
                <CTALink
                  modifier="primary"
                  to={
                    columnHeaderCTA.link?.to ||
                    columnHeaderCTA.link?.toExternal ||
                    columnHeaderCTA.link?.hash ||
                    "#"
                  }
                  noFollow={columnHeaderCTA.link?.noFollow}
                  label={columnHeaderCTA.text}
                  component={`card-${headerColumnTitle}-${columnHeaderTitle}-bottom-cta`}
                />
              )}
            </CellContent>
          </HeaderCell>
        ))}
      </MobileHeaderRow>
    </>
  );
};
